/**
 * KB Analytics - how much knowledge is going in, and how much is coming out.
 *
 * One chart, four lines: your reads and writes, and the team's. Separately each
 * number is a vanity metric; overlaid they are the ratio that matters, because a
 * knowledge base only ever written to is a write-only log, and seeing your own
 * contribution against everyone else's is what makes that actionable.
 *
 * The split is VIEWER-RELATIVE and decided server-side from the credentials on
 * the request, so the SPA is never told who anybody is.
 *
 * Every series comes from tables that already existed - kb_entry.created_at for
 * writes, audit_event for reads - so nothing here needed a new table.
 *
 * Chrome and data live here; the drawing is in kb_analytics_chart.ts.
 */

import { route } from '../../router.js'
import { fetchKBAnalytics, type KBAnalytics } from '../../api/insights.js'
import { action } from '../components/action-delegation.js'
import { esc } from '../../shared/rendering.js'
import { fillDays, renderAnalyticsChart, type Series } from './kb_analytics_chart.js'

/** Windows offered, in days. 90 is the default the server also uses. */
export const WINDOWS = [30, 90, 365] as const

let _data: KBAnalytics | null = null
let _days: number = 90
let _error = false

function renderRangeChips(active: number): string {
  const chips = WINDOWS.map((d) => {
    const on = d === active
    return `<button class="rd-chip${on ? ' active' : ''}" aria-pressed="${on}" ${action('setAnalyticsDays', String(d))}>${String(d)} days</button>`
  }).join('')
  return `<div class="rd-chips" role="group" aria-label="Time window">${chips}</div>`
}

/**
 * The four series, in draw order.
 *
 * Colour carries the MEASURE and dashing carries the SUBJECT: green is always
 * reads, blue is always writes, and the viewer's own pair is the dashed one.
 * Encoding the subject as a third and fourth hue instead would make the reader
 * learn four arbitrary colours; this way they learn two.
 *
 * No identity appears anywhere. "You" is whoever is holding the session, and
 * "Team" is everyone else, so the same project reads differently for each
 * person without the SPA ever being told a name.
 */
export function buildSeries(data: KBAnalytics, days: number): Series[] {
  return [
    {
      key: 'reads-team',
      label: 'Team reads',
      colour: 'grn',
      points: fillDays(
        data.reads.team.map((r) => ({ day: r.day, value: r.reads })),
        days,
      ),
    },
    {
      key: 'writes-team',
      label: 'Team writes',
      colour: 'blu',
      points: fillDays(
        data.writes.team.map((r) => ({ day: r.day, value: r.created })),
        days,
      ),
    },
    {
      key: 'reads-mine',
      label: 'Your reads',
      colour: 'tea',
      dashed: true,
      points: fillDays(
        data.reads.mine.map((r) => ({ day: r.day, value: r.reads })),
        days,
      ),
    },
    {
      key: 'writes-mine',
      label: 'Your writes',
      colour: 'pur',
      dashed: true,
      points: fillDays(
        data.writes.mine.map((r) => ({ day: r.day, value: r.created })),
        days,
      ),
    },
  ]
}

export function renderAnalytics(data: KBAnalytics | null, days: number, error: boolean): string {
  const header = `<header class="page-header">
    <div>
      <h1 class="page-title">Analytics</h1>
      <p class="page-subtitle">Knowledge going in, and knowledge coming out (UTC)</p>
    </div>
  </header>`

  // Chips render in EVERY state. They are the only control on this page, so a
  // loading or failed render without them destroys the button the user just
  // clicked and leaves nothing to click instead.
  if (error) {
    return `${header}${renderRangeChips(days)}<div class="empty">Could not load analytics.</div>`
  }
  if (!data) {
    return `${header}${renderRangeChips(days)}<div class="loading"><div class="spinner"></div></div>`
  }

  return `${header}
    ${renderRangeChips(days)}
    <div class="ka-charts">
      ${renderAnalyticsChart({
        // data.days, not the local `days`: a late response for a window the
        // user has already left would otherwise be sliced to the NEW length for
        // one frame, plotting the old data against the wrong axis.
        series: buildSeries(data, data.days),
        days: data.days,
      })}
    </div>
    <p class="ka-hint">Hover any point for that day's count. Dashed lines are yours;
      solid lines are everyone else on this project, which excludes you so the two
      never double-count the same activity.</p>`
}

function repaint(): void {
  const el = document.getElementById('ka-content')
  if (el) el.innerHTML = renderAnalytics(_data, _days, _error)
}

/**
 * Show the value of whatever point the pointer is over.
 *
 * DELEGATED from the container, which survives every repaint, rather than bound
 * to the 360 circles that do not. Rebinding per repaint would mean 360 listener
 * registrations on every window change and a leak on every one we forgot to
 * remove; the container is recreated on route entry, so these die with it.
 */
function wireTooltip(container: HTMLElement): void {
  container.addEventListener('mousemove', (ev) => {
    const tip = container.querySelector<HTMLElement>('.kl-tip')
    if (!tip) return
    const target = ev.target
    const hit = target instanceof Element ? target.closest('.kl-hit') : null
    const label = hit?.getAttribute('data-label')
    if (!label) {
      tip.hidden = true
      return
    }
    const stage = container.querySelector<HTMLElement>('.kl-stage')
    if (!stage) return
    const box = stage.getBoundingClientRect()
    const colour = hit?.getAttribute('data-colour') ?? 'grn'
    tip.hidden = false
    tip.innerHTML = `<i style="background:var(--${esc(colour)})"></i>${esc(label)}`

    // Positioned AFTER the content is set, because the width depends on it, and
    // flipped to the left of the cursor when it would overhang. Unclamped it ran
    // 200px past the chart on the last day of the window - the most-hovered part
    // of it - and dragged a page-level horizontal scrollbar into existence.
    const width = tip.offsetWidth
    const height = tip.offsetHeight
    const x = ev.clientX - box.left
    const y = ev.clientY - box.top
    const left = x + 12 + width > box.width ? Math.max(0, x - 12 - width) : x + 12
    const top = y + 12 + height > box.height ? Math.max(0, y - 12 - height) : y + 12
    tip.style.left = `${String(Math.round(left))}px`
    tip.style.top = `${String(Math.round(top))}px`
  })

  container.addEventListener('mouseleave', () => {
    const tip = container.querySelector<HTMLElement>('.kl-tip')
    if (tip) tip.hidden = true
  })
}

async function load(): Promise<void> {
  try {
    _data = await fetchKBAnalytics({ days: _days })
    _error = false
  } catch {
    _data = null
    _error = true
  }
  repaint()
}

/** Change the window. Exported for the action registry. */
export function setAnalyticsDays(value: string): void {
  const parsed = Number(value)
  if (!WINDOWS.includes(parsed as (typeof WINDOWS)[number])) return
  if (parsed === _days) return
  _days = parsed
  // Blank the chart while the new window loads, rather than leaving the old
  // range on screen under a freshly-highlighted chip.
  _data = null
  repaint()
  void load()
}

// Types live in action-registry.ts's Window interface augmentation.
window.setAnalyticsDays = setAnalyticsDays

route('/analytics', () => {
  const app = document.getElementById('app')
  if (!app) return
  _data = null
  _error = false
  // The window is NOT reset: someone who chose 365 days and clicked into an
  // entry expects to come back to 365 days.
  document.title = 'Analytics - Grizzly'
  app.innerHTML = '<div id="ka-content"></div>'
  const container = document.getElementById('ka-content')
  if (container) wireTooltip(container)
  repaint()
  void load()
})
